import React, { type ReactNode, useEffect, useState } from "react";
import cx from "classnames";
import { useEventListener } from "usehooks-ts";
import { useIsClient } from "@/hooks/useIsClient";
import {
  Badge,
  BetslipIcon,
  Button,
  NotificationsIcon,
  PageLoader,
  PickslipIcon,
  Pill,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  SearchIcon,
  StarIcon,
  toast,
} from "@picklebet/ui";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { useSignIn } from "@/features/auth/hooks/useSignIn";
import { useBetslipSelectionsCount } from "@/features/betslip/hooks/useBetslipSelectionsCount";
import { usePickemsNotificationCount } from "@/features/pickems/hooks/usePickemsNotificationCount";
import { PerkIcon } from "@/features/perks/components/PerkIcon";
import { type NavPerk, useNavPerks } from "@/features/perks/hooks/useNavPerks";
import { Notification } from "@/features/account/components/notifications/Notification";
import { ContextMenu } from "@/features/account/components/notifications/ContextMenu";
import { QuickBalance } from "@/features/wallet/components/QuickBalance";
import AvailableBalancePillNotice from "@/features/wallet/components/AvailableBalancePillNotice";
import { Search } from "@/features/search/components/Search";
import { BackButton } from "./BackButton";
import { NavLogo } from "./NavLogo";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { useHomeUrl } from "@/hooks/firestore/user/usePreferences";
import {
  type NotificationType,
  useNotifications,
  useUndismissedNotificationsCount,
} from "@/hooks/firestore/user/useNotifications";
import { useIsMobile } from "@/hooks/useDisplay";
import { notificationsService } from "services/notifications";
import { navigate } from "@/utilities/navigate";
import { telemetry } from "@/utilities/telemetry";
import { useDockActions, useRouteDock } from "../hooks/useDock";
import type { DockContent } from "../stores/shellStore";
import type { ShellOptions } from "../types";
import { AppShell } from "./AppShell";
import { TopBar } from "./TopBar";

import * as styles from "./ShellTopBar.module.scss";

const clampBadge = (count: number | null | undefined) =>
  count ? (count >= 10 ? "9+" : count) : undefined;

const NotificationsAction = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const { notifications, loading } = useNotifications(10);
  const badge = useUndismissedNotificationsCount();

  const handleDismiss = (id: string) => {
    setIsOpen(false);
    notificationsService.dismissNotification(id);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        asChild
        onClick={(event) => {
          if (isMobile) {
            event.preventDefault();
            navigate("/account/notifications");
          }
        }}
      >
        <TopBar.Action
          icon={<NotificationsIcon />}
          badge={clampBadge(badge)}
          aria-label="Notifications"
        />
      </PopoverTrigger>
      <PopoverContent sideOffset={20} className={styles.popover} showArrow>
        <div className={styles.notificationsHeader}>
          <h4>Notifications</h4>
          <ContextMenu closePanel={() => setIsOpen(false)} />
        </div>
        <ScrollArea orientation="vertical">
          <div className={styles.notifications}>
            {loading ? (
              <PageLoader />
            ) : (
              notifications.map((notification: NotificationType) => (
                <Notification
                  key={notification.id}
                  notification={notification}
                  onDismissNotification={handleDismiss}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

type PerkProps = Pick<NavPerk, "type" | "name" | "badge" | "pill"> & {
  onClose: () => void;
};

const Perk = ({ type, name, badge, pill, onClose }: PerkProps) => (
  <div className={styles.perk}>
    <div className={styles.perkIcon}>
      <PerkIcon type={type} onClick={onClose} />
      {badge && <Badge className={styles.perkBadge}>{badge}</Badge>}
    </div>
    <div className={styles.perkInfo}>
      <div className={styles.perkName}>{name}</div>
      {pill && (
        <Pill size="small" className={styles.perkPill}>
          {pill}
        </Pill>
      )}
    </div>
  </div>
);

type PerksActionProps = {
  perks: NavPerk[];
  totalCount: number;
};

const PerksAction = ({ perks, totalCount }: PerksActionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <TopBar.Action
          icon={<StarIcon />}
          badge={clampBadge(totalCount)}
          aria-label="Perks"
        />
      </PopoverTrigger>
      <PopoverContent
        sideOffset={20}
        className={cx(styles.popover, styles.perksPopover)}
        showArrow
      >
        <div className={styles.perks}>
          {perks.map((perk) => (
            <Perk
              key={`perk-${perk.type}`}
              type={perk.type}
              name={perk.name}
              badge={perk.badge}
              pill={perk.pill}
              onClose={() => setIsOpen(false)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SearchAction = ({ pathname }: { pathname: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const setModal = useShellStore((state) => state.setModal);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEventListener("keydown", (event) => {
    const target = event.target;
    const isEditable =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement;

    if (
      event.key === "/" &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey &&
      !isMobile &&
      !isEditable
    ) {
      event.preventDefault();
      setIsOpen(!isOpen);
    }
  });

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        asChild
        onClick={(event) => {
          if (isMobile) {
            event.preventDefault();
            setModal("search");
          }
        }}
      >
        <TopBar.Action icon={<SearchIcon />} aria-label="Search" />
      </PopoverTrigger>
      <PopoverContent
        sideOffset={20}
        className={cx(styles.popover, styles.searchPopover)}
        showArrow
      >
        <Search />
      </PopoverContent>
    </Popover>
  );
};

type DockActionProps = {
  content: DockContent;
  icon: ReactNode;
  badge: number;
  label: string;
};

const DockAction = ({ content, icon, badge, label }: DockActionProps) => {
  const { toggleDock } = useDockActions();

  return (
    <TopBar.Action
      icon={icon}
      badge={clampBadge(badge)}
      className={styles.dockTrigger}
      data-dock-trigger={content}
      aria-label={label}
      onClick={() => {
        toast.dismissAll();
        toggleDock(content);
      }}
    />
  );
};

const PickslipAction = () => {
  const badge = usePickemsNotificationCount();

  return (
    <DockAction
      content="pickslip"
      icon={<PickslipIcon />}
      badge={badge}
      label="Pick slip"
    />
  );
};

// Not a direct `.action`, so the crowding ladder doesn't count a trigger
// that never shows below 768.
const BetslipAction = () => {
  const badge = useBetslipSelectionsCount();

  return (
    <span className={styles.betslip}>
      <DockAction
        content="betslip"
        icon={<BetslipIcon />}
        badge={badge}
        label="Betslip"
      />
    </span>
  );
};

const TopBarBalance = () => {
  const { profile, isLoading } = useProfile();

  return (
    <span className={styles.pill}>
      <QuickBalance
        loading={isLoading}
        name={profile?.username || ""}
        avatarSrc={profile?.avatarUri || undefined}
        linkPath="/wallet/overview"
        canHavePopover
      />
      <span className={styles.pillNotice}>
        <AvailableBalancePillNotice />
      </span>
    </span>
  );
};

const AuthActions = () => {
  const setModal = useShellStore((state) => state.setModal);
  const { handleSignIn, isBiometricProcessing } = useSignIn();

  return (
    <div className={styles.auth}>
      <Button
        size="sm"
        variant="secondary"
        loading={isBiometricProcessing}
        onClick={handleSignIn}
      >
        Log In
      </Button>
      <Button
        size="sm"
        onClick={() => {
          setModal("signUp");
          telemetry.signUpClicked({ component: "header_nav" });
        }}
      >
        Sign Up
      </Button>
    </div>
  );
};

// Keyed off the route, so opening the betslip on a Pick'ems page keeps the
// pick slip trigger.
const DockActions = () => {
  const routeDock = useRouteDock();

  return (
    <>
      {routeDock === "pickslip" && <PickslipAction />}
      {routeDock !== "none" && <BetslipAction />}
    </>
  );
};

const LoggedInActions = ({ pathname }: { pathname: string }) => {
  const { perks, totalCount } = useNavPerks();

  return (
    <>
      <NotificationsAction />
      {totalCount > 0 && <PerksAction perks={perks} totalCount={totalCount} />}
      <SearchAction pathname={pathname} />
      <DockActions />
      <TopBarBalance />
    </>
  );
};

const HomeLogo = () => {
  const isClient = useIsClient();
  const { homeUrl, isLoading } = useHomeUrl();

  return (
    <Link to={!isClient || isLoading ? "/" : homeUrl}>
      <NavLogo />
    </Link>
  );
};

type ShellTopBarProps = {
  pathname: string;
  options: ShellOptions;
};

export const ShellTopBar = ({ pathname, options }: ShellTopBarProps) => {
  const isClient = useIsClient();
  const isLoggedIn = useIsLoggedIn();
  const isAuthLoading = useAuthStore((state) => state.status === "loading");
  const isSettled = isClient && !isAuthLoading;

  return (
    <AppShell.TopBar>
      <TopBar className={styles.bar}>
        <TopBar.Leading className={styles.leading}>
          <BackButton animatePresence={options.backButton} />
          <HomeLogo />
        </TopBar.Leading>
        <TopBar.Trailing>
          {isSettled &&
            (isLoggedIn ? (
              <LoggedInActions pathname={pathname} />
            ) : (
              <>
                <SearchAction pathname={pathname} />
                <DockActions />
                <AuthActions />
              </>
            ))}
        </TopBar.Trailing>
      </TopBar>
    </AppShell.TopBar>
  );
};
